import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Select, DatePicker, Button, Input } from "antd";
import Styled from "styled-components";

const { RangePicker } = DatePicker;

// Panel filtrov nad zoznamom.
//
// Filtre su nieco ine ako hladanie: hladanie je jedno policko, do ktoreho
// clovek napise, co ma v hlave, filtre su presne volby (pobocka, sezona,
// obdobie), ktore sa daju kombinovat a vidno na nich, co je prave zapnute.
//
// ROZLOZENIE (prepisane 24.9.2026 po tom, co sa filtre prekryvali).
// Kazdy filter dostane rovnaky diel riadku, nie vlastnu sirku. Na sirokej
// obrazovke su styri v riadku, na tablete dva, na mobile jeden pod druhym.
// Rucne nastavovane sirky sa nescitavali do 24 stlpcov a pri dlhsich
// popisoch sa polia prekryvali. Vlastne sirky uz neberieme do uvahy.
//
// Styly su zamerne obalene vlastnou triedou, lebo v projekte su globalne
// pravidla pre .ant-select (napriklad minimalna sirka 350 px a lavy odstup
// 25 px v hlavicke stranky), ktore by panel rozhodili.
const Obal = Styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 16px 20px 10px;
  margin-bottom: 16px;

  .filter-popis {
    font-size: 12px;
    color: #868eae;
    letter-spacing: .04em;
    text-transform: uppercase;
    margin-bottom: 5px;
    height: 16px;
    line-height: 16px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .ant-select,
  .ant-picker,
  .ant-input {
    width: 100% !important;
    margin: 0 !important;
  }

  .ant-select-selector {
    min-width: 0 !important;
    height: 38px !important;
    display: flex;
    align-items: center;
  }

  .ant-picker,
  .ant-input {
    height: 38px;
  }

  .filter-stlpec {
    min-width: 0;
    margin-bottom: 12px;
  }
`;

// Definicia filtra:
//   { kluc, popis, typ: "vyber" | "obdobie" | "text", moznosti: [{ hodnota, popis }] }
const PanelFiltrov = ({ filtre, hodnoty, onZmena, onVycistit }) => {
  const zoznam = Array.isArray(filtre) ? filtre : [];

  const zapnute = Object.keys(hodnoty || {}).filter((kluc) => {
    const h = hodnoty[kluc];
    return (
      h !== undefined &&
      h !== null &&
      h !== "" &&
      !(Array.isArray(h) && h.length === 0)
    );
  });

  const zmen = (kluc, hodnota) => {
    if (typeof onZmena === "function") {
      onZmena(kluc, hodnota);
    }
  };

  const vykresli = (filter) => {
    if (filter.typ === "text") {
      return (
        <Input
          allowClear
          placeholder={filter.zastupnyText || "hľadať"}
          value={hodnoty[filter.kluc] || ""}
          onChange={(e) => zmen(filter.kluc, e.target.value)}
        />
      );
    }
    // Rozsah cisel, napriklad suma zakazky od a do. Hodnota je dvojica,
    // prazdna strana znamena "neobmedzene".
    if (filter.typ === "rozsah") {
      const par = hodnoty[filter.kluc] || [];
      const zmenCast = (index, text) => {
        const novy = [par[0] ?? "", par[1] ?? ""];
        novy[index] = text;
        const prazdny = String(novy[0]).trim() === "" && String(novy[1]).trim() === "";
        zmen(filter.kluc, prazdny ? undefined : novy);
      };
      return (
        <Input.Group compact style={{ display: "flex" }}>
          <Input
            style={{ flex: 1 }}
            type="number"
            placeholder="od"
            value={par[0] ?? ""}
            onChange={(e) => zmenCast(0, e.target.value)}
          />
          <Input
            style={{ flex: 1 }}
            type="number"
            placeholder="do"
            value={par[1] ?? ""}
            onChange={(e) => zmenCast(1, e.target.value)}
          />
        </Input.Group>
      );
    }
    if (filter.typ === "obdobie") {
      return (
        <RangePicker
          format="D.M.YYYY"
          value={hodnoty[filter.kluc] || null}
          onChange={(rozsah) => zmen(filter.kluc, rozsah)}
          placeholder={["od", "do"]}
        />
      );
    }
    return (
      <Select
        allowClear
        showSearch
        optionFilterProp="children"
        placeholder={filter.zastupnyText || "všetko"}
        value={
          hodnoty[filter.kluc] === undefined ||
          hodnoty[filter.kluc] === null ||
          hodnoty[filter.kluc] === ""
            ? undefined
            : hodnoty[filter.kluc]
        }
        onChange={(hodnota) => zmen(filter.kluc, hodnota)}
        getPopupContainer={(spustac) => spustac.parentNode}
        filterOption={(vstup, moznost) =>
          String(moznost?.children || "")
            .toLowerCase()
            .indexOf(String(vstup || "").toLowerCase()) >= 0
        }
      >
        {(filter.moznosti || []).map((m) => (
          <Select.Option key={String(m.hodnota)} value={m.hodnota}>
            {m.popis}
          </Select.Option>
        ))}
      </Select>
    );
  };

  return (
    <Obal>
      <Row gutter={16}>
        {zoznam.map((filter) => (
          <Col
            key={filter.kluc}
            className="filter-stlpec"
            xs={24}
            sm={12}
            lg={8}
            xxl={6}
          >
            <div className="filter-popis" title={filter.popis}>
              {filter.popis}
            </div>
            {vykresli(filter)}
          </Col>
        ))}
        <Col className="filter-stlpec" xs={24} sm={12} lg={8} xxl={6}>
          <div className="filter-popis">&nbsp;</div>
          <Button
            type="default"
            style={{ width: "100%", height: 38 }}
            disabled={zapnute.length === 0}
            onClick={onVycistit}
          >
            {zapnute.length > 0
              ? `Zrušiť filtre (${zapnute.length})`
              : "Zrušiť filtre"}
          </Button>
        </Col>
      </Row>
    </Obal>
  );
};

// Z hodnot, ktore su naozaj v zozname, spravi moznosti do vyberu.
// Prazdne preskoci, zoradi po slovensky a nepusti duplicity.
const moznostiZoZoznamu = (zoznam, vyber) => {
  const najdene = new Set();
  (Array.isArray(zoznam) ? zoznam : []).forEach((polozka) => {
    const hodnota = vyber(polozka);
    if (hodnota !== undefined && hodnota !== null && String(hodnota).trim() !== "") {
      najdene.add(String(hodnota).trim());
    }
  });
  return Array.from(najdene)
    .sort((a, b) => a.localeCompare(b, "sk"))
    .map((h) => ({ hodnota: h, popis: h }));
};

PanelFiltrov.propTypes = {
  filtre: PropTypes.array.isRequired,
  hodnoty: PropTypes.object,
  onZmena: PropTypes.func,
  onVycistit: PropTypes.func,
};

export { PanelFiltrov, moznostiZoZoznamu };
export default PanelFiltrov;
